import { hapticSelection } from '../../lib/haptics';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
}

/**
 * iOS-style segmented control: a rounded track with a sliding white indicator.
 * Segments are equal-width. Fires a selection haptic on native platforms.
 */
export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
}: SegmentedControlProps<T>) {
  const count = options.length;
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`relative inline-flex w-full rounded-xl bg-gray-100 p-0.5 ${className}`}
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
      }}
    >
      {/* Sliding indicator */}
      <span
        aria-hidden
        className="absolute bottom-0.5 top-0.5 rounded-[10px] bg-white shadow-sm"
        style={{
          left: '2px',
          width: `calc((100% - 4px) / ${count})`,
          transform: `translateX(${activeIndex * 100}%)`,
          transition: 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      />
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              if (!active) {
                void hapticSelection();
                onChange(option.value);
              }
            }}
            className={`relative z-10 flex-1 truncate rounded-[10px] px-3 py-1.5 text-[13px] font-semibold transition-colors touch-manipulation ${
              active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
